import { ref } from "vue"
import { useMutation } from "@tanstack/vue-query"
import { ElMessageBox } from "element-plus"
import http from "@/http-common"

export interface TilatunnusValidationResult {
    isValid: boolean
    isAvailable: boolean
    error?: string
}

export function useTilatunnusValidation() {
    const validationResults = ref<Map<string, TilatunnusValidationResult>>(new Map())
    
    const validateTilatunnus = async (hankeId: number, tilatunnus: string): Promise<boolean> => {
        if (!tilatunnus?.trim()) {
            return true // Empty/null/undefined tilatunnus is valid
        }

        try {
            const response = await http.get(`/tilat/validate`, {
                params: {
                    hankeId,
                    tilatunnus: tilatunnus.trim()
                }
            })
            
            return response.data === true // API returns true if available
        } catch (error) {
            console.error('Tilatunnus validation failed:', error)
            throw error
        }
    }

    const { 
        data: validationResult,
        error: validationError,
        isPending: validationLoading,
        mutateAsync: validateAsync,
        reset: resetValidation
    } = useMutation({
        mutationFn: ({ hankeId, tilatunnus }: { hankeId: number, tilatunnus: string }) =>
            validateTilatunnus(hankeId, tilatunnus)
    })

    const validateTilatunnusList = async (hankeId: number, tilatunnusList: string[]): Promise<{
        valid: string[],
        duplicates: string[],
        errors: string[]
    }> => {
        const valid: string[] = []
        const duplicates: string[] = []
        const errors: string[] = []

        for (const tilatunnus of tilatunnusList) {
            if (!tilatunnus?.trim()) {
                valid.push(tilatunnus)
                continue
            }

            try {
                const isAvailable = await validateTilatunnus(hankeId, tilatunnus)
                if (isAvailable) {
                    valid.push(tilatunnus)
                } else {
                    duplicates.push(tilatunnus)
                }
            } catch (error) {
                errors.push(tilatunnus)
            }
        }

        return { valid, duplicates, errors }
    }

    const clearValidationCache = () => {
        validationResults.value.clear()
        resetValidation()
    }

    const showDuplicateConfirmation = async (duplicates: string[]): Promise<boolean> => {
        try {
            await ElMessageBox.confirm(
                `Tilatunnus ${duplicates.join(', ')} on jo käytössä. Haluatko silti tallentaa?`,
                'Päällekkäisiä tilatunnuksia!',
                {
                    confirmButtonText: 'Tallenna silti',
                    cancelButtonText: 'Peruuta',
                    type: 'warning'
                }
            )
            return true
        } catch {
            return false
        }
    }

    return {
        validationResult,
        validationError,
        validationLoading,
        validateAsync,
        validateTilatunnus,
        validateTilatunnusList,
        clearValidationCache,
        resetValidation,
        showDuplicateConfirmation
    }
}

